import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Device, DeviceStatus } from '../../types/device';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import { Input } from '../../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../../../../components/ui/dialog';
import { formatDate, formatDateLocal, formatDateUTC } from '../../../../utils/unitUtils';
import { WeldTypeNames } from '../../types/device';
import { useDownloadProgress } from '../../../../contexts/DownloadProgressContext';
import { mockProgressService } from '../../../../services/mockProgressService';
import { DownloadProgressTest } from '../../../../components/DownloadProgressTest';
import { WeldParameterDownloadDialog } from '../../../../components/WeldParameterDownloadDialog';
import { DownloadProgressModal } from '../../../../components/DownloadProgressModal';
import { weldParameterDownloadService } from '../../../../services/weldParameterDownloadService';
import {
    Power,
    RefreshCw,
    Check,
    Clock,
    Trash,
    List,
    Download,
    Map,
    Lock,
    ChevronLeft,
    Hash,
    Cpu,
    Building2,
    Activity,
    Settings,
    Calendar,
    Loader2,
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCcw,
    Key,
    Shield,
    History,
    Clock as ClockIcon,
    Monitor,
    Cog,
} from 'lucide-react';
import moment from 'moment';
import { OperationsHeader } from '../../../../components/OperationsHeader';
import { OperationsFilters, TemperatureRanges } from '../../../../components/OperationsFilters';
import { TemperatureControlDashboard } from '../../../../components/TemperatureControlDashboard';
import { RaptorCoatingControlDashboard } from '../../../../components/RaptorCoatingControlDashboard';
import { PressureControlDashboard } from '../../../../components/PressureControlDashboard';
import { ProcessData } from '../../../../components/ProcessData';
import { SystemAlarms } from '../../../../components/SystemAlarms';
import {
    checkCommunication,
    setDeviceTimeDetails,
    getDeviceTimeDetails,
    checkNumberLogs,
    eraseLogs,
    weldParams,
    location,
    passwordProtection,
    updateDeviceOperationStatus,
    clearDeviceOperationsData,
    getAllDevices
} from '../../../../redux/device/actions';
import { FirmwarePage } from './FirmwareUpgrade/FirmwarePage';
import { DeviceLogDetails } from './DeviceLogDetails';
import { DeviceProjectHistory } from './DeviceProjectHistory';

// Status mapping constants (matching main page)
const statusBadgeColors: Record<any, string> = {
    Pending: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    Failed: 'text-readable-red bg-[rgba(196,0,31,0.06)]',
    CertificateAvailable: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    DownloadComplete: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    Deregistered: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
    AdminApprovalPending: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    AdminApproved: "text-galvanizing-green bg-[rgba(31,154,58,0.08)]",
    AdminRejected: 'bg-page-bg text-heritage-grey border border-[#E2E5EA]',
    Register: 'text-[#B38600] bg-[rgba(230,161,0,0.08)]',
    PublishReady: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]',
    AdminApproval: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]',
};

const statusName: Record<any, string> = {
    Pending: 'Registration Pending',
    Failed: 'Registration Failed',
    CertificateAvailable: 'Registration Pending',
    DownloadComplete: 'Registered',
    Deregistered: 'Decommissioned',
    AdminApproval: 'Awaiting Admin Approval',
    AdminApprovalPending: 'Awaiting Admin Approval',
    AdminApproved: "Approved",
    AdminRejected: 'Decommissioned',
    Register: 'Enable Re-registration',
    PublishReady: 'Active',
    connected: 'Connected',
    disconnected: 'Disconnected',
    communicating: 'Communicating',
    online: 'Online',
    offline: 'Offline',
    error: 'Error',
    failed: 'Failed',
    success: 'Success',
    timeout: 'Timeout',
    'in progress': 'In Progress'
};

interface EdgeDeviceManagementProps {
    device: Device;
    onStatusChange: (status: DeviceStatus) => void;
    onReboot: () => void;
    onFirmwareUpdate: () => void;
    onClose: () => void;
    onSettingsClick?: () => void;
    startAutoRefresh?: () => void;
    stopAutoRefresh?: () => void;
    refetchDevice?: () => void;
}

const EdgeDeviceManagement: React.FC<EdgeDeviceManagementProps> = ({
    device,
    onStatusChange,
    onReboot,
    onFirmwareUpdate,
    onClose,
    onSettingsClick,
    startAutoRefresh,
    stopAutoRefresh,
    refetchDevice,
}) => {
    const dispatch = useDispatch();


    // Helper functions for status display
    const getStatusBadgeColor = (status: DeviceStatus) => {
        return statusBadgeColors[status] || 'bg-page-bg text-heritage-grey border border-[#E2E5EA]';
    };

    const formatLocalTimestamp = (date: Date): string => {
        // console
        return moment.utc(date, "DD-MM-YYYY HH:mm:ss.SSS").local().format("DD-MMM-YYYY HH:mm:ss");
    };

    const [apiResponse, setResult] = useState<any>({ hottest: undefined });

    const getStatusName = (status: DeviceStatus) => {
        return statusName[status] || status.charAt(0).toUpperCase() + status.slice(1);
    };
    const [activeTab, setActiveTab] = useState('control');
    const [password, setPassword] = useState('');
    const [pollingProgress, setPollingProgress] = useState<{ [deviceId: string]: any }>({});
    const [isAnyOperationLoading, setIsAnyOperationLoading] = useState(false);
    const [lastOperationTime, setLastOperationTime] = useState(new Date().toISOString());
    // const [isInitialLoad, setIsInitialLoad] = useState(true);
    
    // Local loading states to track when THIS device initiates actions - must be declared early
    const [localCommLoading, setLocalCommLoading] = useState(false);
    const [localTimeLoading, setLocalTimeLoading] = useState(false);
    const [localSetTimeLoading, setLocalSetTimeLoading] = useState(false);
    const [localLogsLoading, setLocalLogsLoading] = useState(false);
    const [localEraseLogsLoading, setLocalEraseLogsLoading] = useState(false);
    const [localWeldParamsLoading, setLocalWeldParamsLoading] = useState(false);
    const [localLocationLoading, setLocalLocationLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    useEffect(() => {
        setActiveTab('control');
        // Clear any previous device-specific data when modal opens
        // This ensures fresh state for each device
        //   dispatch(clearDeviceOperationsData());
        
        // Reset all local loading states when device changes
        setLocalCommLoading(false);
        setLocalTimeLoading(false);
        setLocalSetTimeLoading(false);
        setLocalLogsLoading(false);
        setLocalEraseLogsLoading(false);
        setLocalWeldParamsLoading(false);
        setLocalLocationLoading(false);
    }, [device.id, dispatch]);


    
//  useEffect(() => {
//     console.log('Fetched device', device);
//     console.log('device.firmware_update', (device as any).firmware_update);
   
//    }, [device]);


    // Listen for polling progress updates
    useEffect(() => {
        const handlePollingProgress = (event: any) => {
            if (event.detail && event.detail.deviceId === device.id) {
                setPollingProgress(prev => ({
                    ...prev,
                    [device.id]: event.detail
                }));

                // Handle weld parameter download progress updates
                if (event.detail.data ) {
                    console.log('Weld parameter polling update received:', event.detail.data.weld_parameter_logs);
                    weldParameterDownloadService.handlePollingUpdate(
                        device.id,
                        device.name || `Device ${device.id}`,
                        event.detail.data
                    );
                }
            }
        };

        // Listen for custom polling progress events
        window.addEventListener('pollingProgress', handlePollingProgress);

        return () => {
            window.removeEventListener('pollingProgress', handlePollingProgress);
        };
    }, [device.id, device.name]);

    const handleBackNavigation = () => {
        setActiveTab('control');
        onClose();
    };

    // Redux state selectors
    const deviceState = useSelector((state: any) => state.deviceData || {});
    const {
        communicationCheck = {},
        communicationLoading = false,
        communicationError = null,
        communicationResponseTime = {},
        deviceTime = {},
        timeLoading = false,
        timeError = null,
        setTimeLoading = false,
        setTimeError = null,
        logsManagement = {},
        logsManagementLoading = false,
        logsManagementError = null,
        eraseLogs: eraseLogsData = {},
        eraseLogsLoading = false,
        eraseLogsError = null,
        weldParams: weldParamsData = {},
        weldParamsLoading = false,
        weldParamsError = null,
        location: locationData = {},
        locationLoading = false,
        locationError = null,
        operationStatusUpdate = {},
        operationStatusLoading = false
    } = deviceState;

    // Get device-specific data
    const deviceCommunication = communicationCheck[device.id];
    const deviceResponseTime = communicationResponseTime[device.id];

    // Use communicationData directly from deviceCommunication
    const communicationData = deviceCommunication?.data || deviceCommunication;
    const deviceTimeData = deviceTime[device.id];
    const deviceSetTimeData = deviceTime[device.id + '_set']; // Separate data for set time
    const deviceLogs = logsManagement[device.id];
    const deviceEraseLogs = eraseLogsData[device.id];
    const deviceWeldParams = weldParamsData[device.id];
    // Get the timestamp/updateCount to force re-renders when state updates
    const weldParamsUpdateKey = deviceWeldParams?._timestamp || deviceWeldParams?._updateCount || 0;
    const deviceLocationData = locationData[device.id];
    const deviceOperationStatus = operationStatusUpdate[device.id];

    // Helper function to check if communication data has error
    const hasCommunicationError = (commData: any) => {
        if (!commData) return false;
        // Check for Error field in data.Error (API response structure)
        if (commData.data?.Error && commData.data.Error !== '') {
            return true;
        }
        // Check for Error field in data.data.Error (nested API response structure)
        if (commData.data?.data?.Error && commData.data.data.Error !== '') {
            return true;
        }
        // Check for Error field directly in data (when data is the API response object)
        if (commData.Error && commData.Error !== '') {
            return true;
        }
        // Check for error field at top level
        if (commData.error) {
            return true;
        }
        // Check for Timeout status
        if (commData.status === 'Timeout') {
            return true;
        }
        return false;
    };

    // Helper function to check if data is in progress
    const isDataInProgress = (data: any) => {
        if (!data) return false;
        if (data === 'In Progress' || data === '') return true;
        if (typeof data === 'object') {
            // If there's an Error field, it's not in progress
            if (data.Error) return false;
            if (data.data === 'In Progress' || data.data === '') return true;
            if (data.status === 'In Progress') return true;
        }
        return false;
    };

    // Helper function to check if data has an error
    const hasError = (data: any) => {
        if (!data) return false;
        if (typeof data === 'object') {
            // Check for Error field in the response object
            if (data.Error) return true;
            if (data.data?.Error) return true;
            if (data.error) return true;
            if (data.status === 'Timeout') return true;
        }
        return false;
    };

    // Device-specific loading states - combine local state with device data status
    // A device is loading if: local state is true OR device data has "In Progress" status
    // BUT NOT if it has an error (Error field, error field, or Timeout status)
    const isDeviceCommunicationLoading = (localCommLoading || 
         isDataInProgress(deviceCommunication?.data) || 
         isDataInProgress(deviceCommunication)) && 
         !hasCommunicationError(deviceCommunication);
    
    const isDeviceTimeLoading = (localTimeLoading || 
         isDataInProgress(deviceTimeData?.data) || 
         isDataInProgress(deviceTimeData)) && 
         !hasError(deviceTimeData?.data) && 
         !hasError(deviceTimeData);
    
    const isDeviceSetTimeLoading = (localSetTimeLoading || 
         isDataInProgress(deviceSetTimeData?.data) || 
         isDataInProgress(deviceSetTimeData)) && 
         !hasError(deviceSetTimeData?.data) && 
         !hasError(deviceSetTimeData);
    
    const isDeviceLogsLoading = (localLogsLoading || 
         isDataInProgress(deviceLogs?.data) || 
         isDataInProgress(deviceLogs)) && 
         !hasError(deviceLogs?.data) && 
         !hasError(deviceLogs);
    
    const isDeviceEraseLogsLoading = (localEraseLogsLoading || 
         isDataInProgress(deviceEraseLogs?.data) || 
         isDataInProgress(deviceEraseLogs)) && 
         !hasError(deviceEraseLogs?.data) && 
         !hasError(deviceEraseLogs);
    
    const isDeviceWeldParamsLoading = localWeldParamsLoading || 
         isDataInProgress(deviceWeldParams?.data) || 
         isDataInProgress(deviceWeldParams) ||
         (typeof deviceWeldParams?.data === 'string' && 
          (deviceWeldParams.data.includes('In Progress') || 
           deviceWeldParams.data.includes('Processing') ||
           deviceWeldParams.data.includes('chunks processed')));
    
    const isDeviceLocationLoading = (localLocationLoading || 
         isDataInProgress(deviceLocationData?.data) || 
         isDataInProgress(deviceLocationData)) && 
         !hasError(deviceLocationData?.data) && 
         !hasError(deviceLocationData);

    // Device-specific error states - only show errors for this device
    // Check for Error field in data object, error field, or status === 'Timeout'
    // The API response structure can be: data.Error or data.data.Error
    const getCommunicationError = () => {
        if (!deviceCommunication) return null;
        // Check Error field in data.data (API response structure)
        if (deviceCommunication.data?.Error) {
            return deviceCommunication.data.Error;
        }
        // Check Error field in data (when data is the API response object)
        if (deviceCommunication.data?.data?.Error) {
            return deviceCommunication.data.data.Error;
        }
        // Check error field at top level
        if (deviceCommunication.error) {
            return deviceCommunication.error;
        }
        // Check for Timeout status
        if (deviceCommunication.status === 'Timeout') {
            return deviceCommunication.error || 'Timeout';
        }
        // Check global error if device communication exists
        if (communicationError && deviceCommunication) {
            return communicationError;
        }
        return null;
    };
    const deviceCommunicationError = getCommunicationError();
    
    const deviceTimeError = deviceTimeData?.data?.Error || 
        deviceTimeData?.error || 
        device.get_time?.error ||
        (timeError && deviceTimeData ? timeError : null);
    
    const deviceSetTimeError = deviceSetTimeData?.data?.Error || 
        deviceSetTimeData?.error || 
        device.set_time?.error ||
        (setTimeError && deviceSetTimeData ? setTimeError : null);
    
    const deviceLogsError = deviceLogs?.data?.Error || 
        deviceLogs?.error || 
        device.number_of_logs?.error ||
        (logsManagementError && deviceLogs ? logsManagementError : null);
    
    const deviceEraseLogsError = deviceEraseLogs?.data?.Error || 
        deviceEraseLogs?.error || 
        device.erase_logs?.error ||
        (eraseLogsError && deviceEraseLogs ? eraseLogsError : null);
    
    const deviceWeldParamsError = deviceWeldParams?.data?.Error || 
        deviceWeldParams?.error || 
        device.weld_parameter_logs?.error ||
        (weldParamsError && deviceWeldParams ? weldParamsError : null);
    
    const deviceLocationError = deviceLocationData?.data?.Error || 
        deviceLocationData?.error || 
        (device.location as any)?.error ||
        (locationError && deviceLocationData ? locationError : null);
    
    // Extract location error for type safety
    const deviceLocationPropError = (device.location as any)?.error;

    // State for new UI
    const [isConnected, setIsConnected] = useState(device.communication_status === 'Online');
    const [lastCommCheck, setLastCommCheck] = useState(new Date().toLocaleString());
    const [currentDeviceTime, setCurrentDeviceTime] = useState(new Date().toLocaleString());
    const [isInitialLoad, setIsInitialLoad] = useState(false);
    const [commCheckStartTime, setCommCheckStartTime] = useState<number | null>(null);
    const [timeInput, setTimeInput] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    });
    const [logs, setLogs] = useState(deviceLogs?.data || 0);
    const [erasing, setErasing] = useState(false);
    const [isEraseLogsConfirmOpen, setIsEraseLogsConfirmOpen] = useState(false);
    const [downloadingParams, setDownloadingParams] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showWeldParamConfirmDialog, setShowWeldParamConfirmDialog] = useState(false);
    const [deviceLocation, setDeviceLocation] = useState({
        station: "-",
        type: "-",
        number: "-"
    });

    // Operations data - dummy values
    const [operationsData] = useState({
        mode: 'Joint',
        jointNumber: 1234,
        isUpdating: false,
        lastUpdate: new Date().toLocaleString()
    });


    // Process Data - dummy values
    const [processData] = useState({
        jointVolume: { actual: 45.2, unit: 'Litres' },
        chargeVolume: { actual: 12.8, unit: 'Litres' },
        fillTime: { actual: 154, unit: 'Seconds' },
        totalTime: { actual: 312, unit: 'Seconds' }
    });

    // Checkbox states for Operations filters
    const [showTemperatureZones, setShowTemperatureZones] = useState(true);
    const [showPressureSystems, setShowPressureSystems] = useState(true);
    const [showProcessData, setShowProcessData] = useState(true);
    const [showAlarms, setShowAlarms] = useState(true);

    // Temperature range configuration
    const [temperatureRanges, setTemperatureRanges] = useState<TemperatureRanges>({
        normal: { min: 245, max: 255 },
        warning: { min: 0, max: 244 },
    });
    const [refreshInterval, setRefreshInterval] = useState<number>(5); // Default 5 minutes

    // Temperature Control Dashboard - updatable state for polling
    const [temperatureView, setTemperatureView] = useState<'dashboard' | 'grid' | 'table' | 'warnings' | 'danger'>('dashboard');
    
    // Helper function to calculate status based on deviation
    // const calculateTemperatureStatus = (actual: number, setpoint: number): 'normal' | 'warning' => {
    //     const deviation = Math.abs(actual - setpoint);
    //     // Consider warning if deviation is more than 3°C
    //     return deviation > 3 ? 'warning' : 'normal';
    // };


    // Pressure Control Dashboard - dummy values
    const [pressureView, setPressureView] = useState<'dashboard' | 'grid' | 'table' | 'warnings' | 'danger'>('dashboard');

    // Determine coating type from device metadata (no need to wait for /coating-data response)
    const deviceCoatingType = (device.deviceData?.coating_type || device.deviceData?.coatingType || '').toString().toLowerCase();
    const isRaptorDevice = deviceCoatingType === 'raptor';
    const [raptorConfig, setRaptorConfig] = useState<{
        fbe: { alarmLow: number; warningLow: number; warningHigh: number; alarmHigh: number };
        cmpp: { alarmLow: number; warningLow: number; warningHigh: number; alarmHigh: number };
    }>(() => {
        const fromDevice = (device.deviceData as any)?.deviceConfiguration;
        const base = {
            fbe: { alarmLow: -0.5, warningLow: -0.1, warningHigh: 0.1, alarmHigh: 0.5 },
            cmpp: { alarmLow: -0.5, warningLow: -0.1, warningHigh: 0.1, alarmHigh: 0.5 },
        };
        if (fromDevice?.fbe && fromDevice?.cmpp) {
            return {
                fbe: {
                    alarmLow: Number(fromDevice.fbe.alarmLow),
                    warningLow: Number(fromDevice.fbe.warningLow),
                    warningHigh: Number(fromDevice.fbe.warningHigh),
                    alarmHigh: Number(fromDevice.fbe.alarmHigh),
                },
                cmpp: {
                    alarmLow: Number(fromDevice.cmpp.alarmLow),
                    warningLow: Number(fromDevice.cmpp.warningLow),
                    warningHigh: Number(fromDevice.cmpp.warningHigh),
                    alarmHigh: Number(fromDevice.cmpp.alarmHigh),
                },
            };
        }
        return base;
    });

    // If API returns deviceConfiguration for raptor, hydrate thresholds from it.
    useEffect(() => {
        if (!isRaptorDevice) return;
        const cfg = (apiResponse as any)?.deviceConfiguration;
        if (!cfg?.fbe || !cfg?.cmpp) return;

        const next = {
            fbe: {
                alarmLow: Number(cfg.fbe.alarmLow),
                warningLow: Number(cfg.fbe.warningLow),
                warningHigh: Number(cfg.fbe.warningHigh),
                alarmHigh: Number(cfg.fbe.alarmHigh),
            },
            cmpp: {
                alarmLow: Number(cfg.cmpp.alarmLow),
                warningLow: Number(cfg.cmpp.warningLow),
                warningHigh: Number(cfg.cmpp.warningHigh),
                alarmHigh: Number(cfg.cmpp.alarmHigh),
            },
        };

        // Avoid unnecessary state updates every poll
        if (JSON.stringify(next) !== JSON.stringify(raptorConfig)) {
            setRaptorConfig(next);
        }
    }, [isRaptorDevice, apiResponse?.deviceConfiguration]);

    // Progress tracking
    const { addDownload, updateProgress, completeDownload, failDownload, activeDownloads } = useDownloadProgress();
    const [currentDownloadId, setCurrentDownloadId] = useState<string | null>(null);
    const lastProcessedMessageRef = useRef<string>('');

    // Initialize the weld parameter download service context when component mounts
    useEffect(() => {
        weldParameterDownloadService.setContext({ addDownload, updateProgress, completeDownload, failDownload, activeDownloads });
    }, [addDownload, updateProgress, completeDownload, failDownload, activeDownloads]);

    // Calculate hottest temperature from temperatureData
    // useEffect(() => {
    //     if (temperatureData.length > 0) {
    //         const hottest = Math.max(...temperatureData.map(t => t.actual));
    //         setResult({ hottest });
    //     }
    // }, [temperatureData]);

    // Ref to store the interval ID so we can update it dynamically
    const temperaturePollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    // Ref to store the fetchTemperatureData function so it can be accessed from outside the useEffect
    const fetchTemperatureDataRef = useRef<(() => Promise<void>) | null>(null);
    // Prevent overlapping /coating-data calls (interval + manual refresh + effect reruns)
    const isFetchingCoatingDataRef = useRef<boolean>(false);
    // Ref to track current activeTab value (to avoid stale closure issues in intervals)
    const activeTabRef = useRef<string>(activeTab);
    // Ref to track showTemperatureZones value (to avoid stale closure issues in intervals)
    const showTemperatureZonesRef = useRef<boolean>(showTemperatureZones);

    // Keep refs in sync with state (to avoid stale closure issues)
    useEffect(() => {
        activeTabRef.current = activeTab;
        
        // Immediately clear interval if we're not on operations tab
        if (activeTab !== 'operations' && temperaturePollingIntervalRef.current) {
            console.log('Tab changed to non-operations, clearing interval immediately');
            clearInterval(temperaturePollingIntervalRef.current);
            temperaturePollingIntervalRef.current = null;
        }
    }, [activeTab]);
    
    useEffect(() => {
        showTemperatureZonesRef.current = showTemperatureZones;
        
        // Immediately clear interval if temperature zones are hidden
        if (!showTemperatureZones && temperaturePollingIntervalRef.current) {
            clearInterval(temperaturePollingIntervalRef.current);
            temperaturePollingIntervalRef.current = null;
        }
    }, [showTemperatureZones]);

    // Function to manually refresh temperature data (can be called from OperationsFilters refresh button)
    // IMPORTANT: return the Promise so callers can show a loading/spinner state until the fetch completes.
    const handleRefreshTemperatureData = React.useCallback(async (): Promise<void> => {
        if (!fetchTemperatureDataRef.current) return;
        await fetchTemperatureDataRef.current();
    }, []);

    // Function to update the polling interval (can be called from OperationsFilters after save)
    const updatePollingInterval = React.useCallback((refreshIntervalSeconds: number) => {
        // Only update if we're on the operations tab and temperature zones are shown

        // For coating-data APIs, edge_uid corresponds to the Edge/Weld UID (metadata.weldUID).
        // Fallback to device.id if weldUID is not available for some reason.
        const edgeUidForCoating = device.metadata?.weldUID || device.id;

        if (activeTab !== 'operations' || !showTemperatureZones || !edgeUidForCoating) {
            return;
        }

        // Clear existing interval if any
        if (temperaturePollingIntervalRef.current) {
            clearInterval(temperaturePollingIntervalRef.current);
            temperaturePollingIntervalRef.current = null;
        }

        // Convert seconds to milliseconds
        const intervalMs = refreshIntervalSeconds * 1000;

        // Convert seconds to minutes for state (for UI display)
        const intervalMinutes = refreshIntervalSeconds / 60;
        setRefreshInterval(intervalMinutes);

        // Set up new interval with updated timing
        if (fetchTemperatureDataRef.current) {
            temperaturePollingIntervalRef.current = setInterval(fetchTemperatureDataRef.current, intervalMs);
            // console.log(`Updated polling interval to ${intervalMs}ms (${refreshIntervalSeconds} seconds)`);
        }
    }, [activeTab, showTemperatureZones, device.metadata?.weldUID, device.id]);

    // Polling for temperature data when on Operations tab
    useEffect(() => {
        let cancelled = false;
        const abortController = new AbortController();

        // Only poll if Operations tab is active and Temperature Zones checkbox is checked
        // Also check if device.id exists (device.metadata?.weldUID might not be available initially)
        if (!device.id) {
            // console.log('Skipping fetchTemperatureData - device.id not available');
            return;
        }
        
        // Only fetch when on operations tab and temperature zones are shown
        if (activeTab !== 'operations' || !showTemperatureZones) {
            // console.log('Skipping fetchTemperatureData - activeTab:', activeTab, 'showTemperatureZones:', showTemperatureZones);
            return;
        }

        const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || 'http://localhost:9000';
        const edgeUidForCoating = device.metadata?.weldUID || device.id;
        const endpoint = `${apiUrl}/coating-data?edge_uid=${edgeUidForCoating}`;
        
        // Default interval: 3 seconds (3000ms)
        let currentInterval = 20000;

        // Function to fetch and update temperature data
        const fetchTemperatureData = async () => {
            // Double-check we're still on operations tab before making API call
            // Use refs to get current values (not closure values) - prevents API calls when user has switched to another tab
            if (activeTabRef.current !== 'operations' || !showTemperatureZonesRef.current) {
                // console.log('🚫 BLOCKED fetchTemperatureData - no longer on operations tab or temperature zones hidden. Current tab:', activeTabRef.current, 'showTemperatureZones:', showTemperatureZonesRef.current);
                // Clear interval if we're not on operations tab anymore
                if (activeTabRef.current !== 'operations' && temperaturePollingIntervalRef.current) {
                    clearInterval(temperaturePollingIntervalRef.current);
                    temperaturePollingIntervalRef.current = null;
                    // console.log('✅ Cleared interval - no longer on operations tab');
                }
                return;
            }

            try {
                // Prevent overlapping calls; otherwise multiple intervals or manual refresh can stack requests
                if (isFetchingCoatingDataRef.current) {
                    return;
                }
                isFetchingCoatingDataRef.current = true;
                if (cancelled) return;

                // console.log('✅ Fetching temperature data from:', endpoint, 'Current tab:', activeTabRef.current);
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const coatingType = responseData?.data?.coatingType ?? responseData?.coatingType ?? null;
                const deviceConfiguration =
                    responseData?.data?.deviceConfiguration ??
                    responseData?.deviceConfiguration ??
                    responseData?.data?.data?.deviceConfiguration ??
                    null;
                const data = responseData?.data?.data;
                // console.log('Temperature data received:', data);
                
                // Double-check again after async operation (tab might have changed during fetch)
                // Use refs to get current values
                if (activeTabRef.current !== 'operations' || !showTemperatureZonesRef.current) {
                    // console.log('Skipping state update - no longer on operations tab or temperature zones hidden. Current tab:', activeTabRef.current, 'showTemperatureZones:', showTemperatureZonesRef.current);
                    return;
                }
                if (cancelled) return;
                
                // Process the API response
                // Note: for coatingType "raptor", the payload does not contain satDifferences; still store it.
                if (data) {
                    const merged = (data && typeof data === 'object')
                        ? { ...data, coatingType, deviceConfiguration }
                        : data;
                    setResult(merged);

                    // Update polling interval if refresh_interval_seconds is provided
                    // BUT ONLY if we're still on operations tab
                    // If API doesn't send refresh_interval_seconds (common for some coating types),
                    // fall back to 20s. IMPORTANT: do NOT use data.refresh_interval_seconds directly
                    // for setInterval math, otherwise undefined -> NaN -> 0ms interval (spam calls).
                    const refreshSecondsRaw = (data as any).refresh_interval_seconds;
                    const refreshSeconds =
                        typeof refreshSecondsRaw === 'number' && Number.isFinite(refreshSecondsRaw) && refreshSecondsRaw > 0
                            ? refreshSecondsRaw
                            : 20;

                    console.log('data.refresh_interval_seconds.....', refreshSeconds);
                    if (refreshSeconds !== undefined) {
                        // Triple-check we're still on operations tab before setting up new interval
                        if (activeTabRef.current !== 'operations' || !showTemperatureZonesRef.current) {
                            // console.log('Skipping interval setup - no longer on operations tab. Current tab:', activeTabRef.current);
                            return;
                        }
                        if (cancelled) return;
                        
                        // Clear existing interval if any
                        if (temperaturePollingIntervalRef.current) {
                            clearInterval(temperaturePollingIntervalRef.current);
                            temperaturePollingIntervalRef.current = null;
                        }

                        // Use API value (convert seconds to milliseconds)
                        const intervalMs = refreshSeconds * 1000;
                        currentInterval = intervalMs;

                        // Convert seconds to minutes for state (for UI display)
                        const intervalMinutes = refreshSeconds / 60;
                        console.log('intervalMinutes.....', intervalMinutes);
                        setRefreshInterval(intervalMinutes);

                        // Set up new interval with updated timing - but only if still on operations tab
                        if (activeTabRef.current === 'operations' && showTemperatureZonesRef.current) {
                            temperaturePollingIntervalRef.current = setInterval(fetchTemperatureData, intervalMs);
                            // console.log(`Updated polling interval to ${intervalMs}ms (${refreshSeconds} seconds)`);
                        } else {
                            // console.log('Not setting up interval - no longer on operations tab');
                        }
                    }
                }
            } catch (error: any) {
                if (error?.name === 'AbortError') {
                    return;
                }
                console.error('Error fetching coating data:', { endpoint, error });
                // Don't update state on error, keep existing data
            } finally {
                isFetchingCoatingDataRef.current = false;
            }
        };

        // Store fetchTemperatureData in ref so it can be accessed from updatePollingInterval
        fetchTemperatureDataRef.current = fetchTemperatureData;

        // Fetch immediately on mount/tab change
        // console.log('Calling fetchTemperatureData - activeTab:', activeTab, 'activeTabRef.current:', activeTabRef.current, 'showTemperatureZones:', showTemperatureZones, 'device.id:', device.id);
        console.log('Calling fetchTemperatureData - currentInterval:', currentInterval);
        fetchTemperatureData();

        // Set up initial polling interval (default: 3 seconds = 3000ms)
        // Only set up interval if we're on operations tab
        if (activeTab === 'operations' && showTemperatureZones) {
            temperaturePollingIntervalRef.current = setInterval(fetchTemperatureData, currentInterval);
            // console.log('Set up initial polling interval:', currentInterval, 'ms');
        } else {
            // console.log('Skipping initial interval setup - not on operations tab or temperature zones hidden');
        }

        // Cleanup interval on unmount or when conditions change
        return () => {
            cancelled = true;
            abortController.abort();
            // console.log('Cleaning up temperature polling interval - activeTab:', activeTab, 'showTemperatureZones:', showTemperatureZones);
            if (temperaturePollingIntervalRef.current) {
                clearInterval(temperaturePollingIntervalRef.current);
                temperaturePollingIntervalRef.current = null;
                // console.log('Interval cleared in cleanup');
            }
            fetchTemperatureDataRef.current = null;
        };
    }, [activeTab, showTemperatureZones, device.id, device.metadata?.weldUID]);

    // Cleanup mock progress service on unmount and resume auto-refresh if needed
    useEffect(() => {
        return () => {
            if (currentDownloadId) {
                mockProgressService.stopMockDownload(currentDownloadId);
            }
            // Resume auto-refresh on unmount if it was stopped for download
            if (downloadingParams && startAutoRefresh) {
                startAutoRefresh();
                // console.log('startAutoRefresh called on unmount');
            }
        };
    }, [currentDownloadId, downloadingParams, startAutoRefresh]);

    // Determine which tabs to show based on device process type
    const isCoatingDevice = device.deviceData?.process_type === 'coating';
    const tabs = isCoatingDevice 
        ? ["Control", "Firmware", "Certificates", "Logs", "History", "Operations"] 
        : ["Control", "Firmware", "Certificates", "Logs", "History"];
    // // Auto-refresh device time when component mounts
  //  console.log('device', device);
    // useEffect(() => {
    //     if (activeTab === 'get_Time') {
    //         dispatch(getDeviceTimeDetails({ deviceId: device.id }));
    //     }
    // }, [activeTab, device.id, dispatch]);



    // useEffect(() => {
    //     // Dispatch the action with payload
    //     dispatch(getAllDevices({
    //         deviceId: device.id,
    //         weldType: device.metadata.weldType,
    //         weldUID: device.metadata.weldUID,
    //         usePolling: true
    //     }));

    //     // Access the results from Redux state

    // }, [device.id, dispatch]);

    // const allDevicesData = useSelector((state: any) => state.deviceData.allDevicesData);
    // const deviceData = allDevicesData[device.id];
    // console.log('deviceData', deviceData);
    // Auto-call APIs when modal opens - sequential approach
    // useEffect(() => {
    //     if(isInitialLoad){
    //     // First call communication check
    //     handleCommunicationCheck();
    //     }

    //     // // Set initial load to false after a short delay
    //     // const timer = setTimeout(() => {
    //     //     setIsInitialLoad(false);
    //     // }, 2000);

    //     // return () => clearTimeout(timer);
    // }, [device.id]); // Only run when device changes

    // // Call other APIs only after communication check succeeds
    // useEffect(() => {
    //     if ( isInitialLoad && communicationData?.connection_status === 'Online' && !communicationLoading) {
    //         setIsInitialLoad(false);
    //         // Communication check succeeded, now call other APIs
    //         handleGetTime();
    //         handleCheckLogs();
    //         handleLocation();
    //         //setIsInitialLoad(false);
    //     }
    // }, [communicationData?.connection_status, communicationLoading, device.id]);

    // Check if any operation is loading for THIS device only
    useEffect(() => {
        const anyLoading = isDeviceCommunicationLoading ||
            isDeviceTimeLoading ||
            isDeviceSetTimeLoading ||
            isDeviceLogsLoading ||
            isDeviceEraseLogsLoading ||
            isDeviceWeldParamsLoading ||
            isDeviceLocationLoading ||
            (operationStatusLoading && deviceOperationStatus);
        setIsAnyOperationLoading(anyLoading);
    }, [isDeviceCommunicationLoading, isDeviceTimeLoading, isDeviceSetTimeLoading, isDeviceLogsLoading, isDeviceEraseLogsLoading, isDeviceWeldParamsLoading, isDeviceLocationLoading, operationStatusLoading, deviceOperationStatus]);

    // Helper functions
    const handleCommunicationCheck = () => {
        setLocalCommLoading(true);
        dispatch(checkCommunication({ deviceId: device.id, command: 'CommCheck' }));
    };

    const handleSetTime = () => {
        if (timeInput) {
            setLocalSetTimeLoading(true);
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');

            // Get timezone offset in format +05:30 or -05:30
            const timezoneOffset = now.getTimezoneOffset();
            const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
            const offsetMinutes = Math.abs(timezoneOffset) % 60;
            const timezoneSign = timezoneOffset <= 0 ? '+' : '-';
            const timezoneString = `${timezoneSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

            const new_time = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${timezoneString}`;

            setTimeInput(new_time);
            dispatch(setDeviceTimeDetails({
                deviceId: device.id,// Use different key for set time
                command: 'SetTime',
                weldType: device.metadata.weldType,
                weldUID: device.metadata?.weldUID,
                timeDetails: { New_Time: new_time }
            }));
        }
    };

    const handleGetTime = () => {
        setLocalTimeLoading(true);
        dispatch(getDeviceTimeDetails({ deviceId: device.id, command: 'GetTime', weldType: device.metadata.weldType, weldUID: device.metadata.weldUID }));
    };

    const handleCheckLogs = () => {
        setLocalLogsLoading(true);
        dispatch(checkNumberLogs({ deviceId: device.id, command: 'NumOfLogs', weldType: device.metadata.weldType, weldUID: device.metadata.weldUID }));
    };

    const handleEraseLogs = () => {
        setLocalEraseLogsLoading(true);
        dispatch(eraseLogs({ deviceId: device.id, command: 'EraseLogs', weldType: device.metadata.weldType, weldUID: device.metadata.weldUID }));
    };

    const handleWeldParams = () => {
        // Check if weld parameters are already downloaded
        const weldParamsData = device.weld_parameter_logs?.data;
        if (weldParamsData === 'Weld Parameter has been downloaded successfully') {
            // Show confirmation dialog for re-download
            setShowWeldParamConfirmDialog(true);
            return;
        }

        // Start the download process
        startWeldParameterDownload();
    };

    const startWeldParameterDownload = () => {
        setLocalWeldParamsLoading(true);
        setDownloadingParams(true); // Set downloading state to true
        
        // Stop auto-refresh to prevent device list from refreshing during download
        if (stopAutoRefresh) {
            stopAutoRefresh();
        }
        
        // Start progress tracking
        const downloadId = weldParameterDownloadService.startDownload(
            device.id, 
            device.name || `Device ${device.id}`
        );
        setCurrentDownloadId(downloadId);

        // Dispatch the actual API call
        dispatch(weldParams({ deviceId: device.id, command: 'WeldParams', weldType: device.metadata.weldType, weldUID: device.metadata.weldUID }));

        // Show download progress modal
        setShowDownloadModal(true);
    };

    const handleConfirmWeldParamDownload = () => {
        setShowWeldParamConfirmDialog(false);
        startWeldParameterDownload();
    };

    const handleLocation = () => {
        setLocalLocationLoading(true);
        dispatch(location({ deviceId: device.id, command: 'Location', weldType: device.metadata.weldType, weldUID: device.metadata.weldUID }));
    };

    const handlePasswordProtection = () => {
        dispatch(passwordProtection({ deviceId: device.id, command: 'PasswordProtection', password }));
    };

    const handleUpdateOperationStatus = (status: string) => {
        dispatch(updateDeviceOperationStatus({
            deviceId: device.id,
            operationStatus: status,
            lastUpdated: new Date().toISOString()
        }));
    };

    // Helper function to format platform value
    const formatPlatform = (platform: string | undefined): string => {
        if (!platform) return '-';
         const lowerPlatform = platform;
        // if (lowerPlatform.includes('imx8a') || lowerPlatform.includes('alpha')) return 'imx_8a';
        // if (lowerPlatform.includes('imx8b') || lowerPlatform.includes('beta')) return 'imx_8b';
        // if (lowerPlatform.includes('imx8ivy') || lowerPlatform.includes('ivy')) return 'imx_8ivy';
        return lowerPlatform; // Return original if no match
    };

    // Helper function to get build type from weld_type
    const getBuildType = (processType: string | undefined, weldType: string | undefined): string | null => {
        if (processType?.toLowerCase() !== 'welding') return null;
        if (!weldType) return null;
        const lowerWeldType = weldType.toLowerCase();
        if (lowerWeldType.includes('m500')) return 'M500';
        if (lowerWeldType.includes('p600z')) return 'P600Z';
        if ( lowerWeldType.includes('p625')) return 'P625';
        return null;
    };

    // Get device information for badges
    const processType = device.deviceData?.process_type || device.deviceData?.processType || '-';
    const platform = formatPlatform(device.deviceData?.platform || '-');
    const buildType = getBuildType(processType, device.deviceData?.weld_type || '-');

    // Color palette for badges
    // Build header details array similar to ProductionTab.tsx
    const headerDetails = useMemo(() => [
        { label: 'Device ID', value: device.id || '-' },
        ...(device.metadata?.weldUID ? [{ label: 'UID', value: device.metadata.weldUID }] : []),
        ...(device.metadata?.ipAddress || device.deviceData?.ipAddress || device.deviceData?.ip_address ? [{ label: 'IP Address', value: device.metadata?.ipAddress || device.deviceData?.ipAddress || device.deviceData?.ip_address }] : []),
        ...(processType && processType !== '-' ? [{ label: 'Process Type', value: typeof processType === 'string' ? processType.charAt(0).toUpperCase() + processType.slice(1) : processType }] : []),
        ...(platform && platform !== '-' ? [{ label: 'Platform', value: platform }] : []),
        ...(buildType ? [{ label: 'Weld Type', value: buildType }] : []),
    ], [device.id, device.metadata?.weldUID, device.metadata?.ipAddress, device.deviceData?.ipAddress, device.deviceData?.ip_address, processType, platform, buildType]);

    const infoItems = headerDetails;



    // Helper function to get location data
    const getLocationData = (field: 'Station' | 'BugType' | 'BugNum') => {
        try {
            // Handle new direct response structure
            if (deviceLocationData?.data?.Data) {
                const parsed = JSON.parse(deviceLocationData.data.Data);
                return parsed[field] || '-';
            }
            // Fallback to old structure
            if (deviceLocationData?.data &&
                deviceLocationData.data !== 'In Progress' &&
                deviceLocationData.data !== '' &&
                deviceLocationData.data !== 'Timeout') {
                const parsed = JSON.parse(deviceLocationData.data);
                return parsed[field] || '-';
            }
            else if (device.location?.parsed && device.location?.parsed[field]) {
                return device.location?.parsed[field] || '-';
            }
        } catch (error) {
        }
        return '-';
    };


    const getLogCount = () => {
        try {
            // Handle new direct response structure
            if (deviceLogs?.data?.Data) {
                return deviceLogs.data.Data;
            }
            // Fallback to old structure
            if (device.number_of_logs?.data && device.number_of_logs) {
                return device.number_of_logs?.data || '-';
            }
        } catch (error) {
        }
        return '-';
    };

    // Utility function to format response time
    const formatResponseTime = (ms: number): string => {
        if (ms < 1000) {
            return `${ms}ms`;
        }

        const minutes = Math.floor(ms / 60000);
        const seconds = Math.floor((ms % 60000) / 1000);
        const milliseconds = ms % 1000;

        let result = '';
        if (minutes > 0) {
            result += `${minutes}m `;
        }
        if (seconds > 0) {
            result += `${seconds}s `;
        }
        if (milliseconds > 0) {
            result += `${milliseconds}ms`;
        }

        return result.trim();
    };

    // Utility function to format device time with proper date parsing
    const formatDeviceTime = (timeData: any): string => {
        try {
            let dateString = '';
            
            // Handle new direct response structure
            if (timeData?.Data) {
                dateString = timeData.Data;
            } 
            // Fallback to old structure
            else if (typeof timeData === 'string') {
                dateString = timeData;
            }
            
            if (!dateString) {
                return 'Invalid Date';
            }
            
            // Fix the timezone format issue: -00:00 should be +00:00 or Z
            const fixedDateString = dateString.replace(/-00:00$/, '+00:00');
            
            const date = new Date(fixedDateString);
            
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            
            // Format as UTC time
            const day = date.getUTCDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
            const year = date.getUTCFullYear();
            const time = date.toLocaleTimeString('en-US', { 
                hour12: false, 
                timeZone: 'UTC',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
            
            return `${day}-${month}-${year} ${time} UTC`;
        } catch (error) {
            return 'Invalid Date';
        }
    };

    // Utility function to format last seen time in days, hours, and minutes
    const formatLastSeenTime = (minutesAgo: number): string => {
        if (minutesAgo < 1) {
            return 'Just now';
        }

        const days = Math.floor(minutesAgo / (24 * 60));
        const hours = Math.floor((minutesAgo % (24 * 60)) / 60);
        const minutes = Math.floor(minutesAgo % 60);

        let result = '';

        if (days > 0) {
            result += `${days} day${days > 1 ? 's' : ''} `;
        }
        if (hours > 0) {
            result += `${hours}h `;
        }
        if (minutes > 0) {
            result += `${minutes}min`;
        }

        return result.trim();
    };

    // Response time is now handled by Redux saga

    // Update logs state when deviceLogs data changes
    useEffect(() => {
        if (deviceLogs?.data) {
            // Check for Error field first - if present, stop loading and don't update logs
            if (hasError(deviceLogs.data) || deviceLogs.data?.Error) {
                setLocalLogsLoading(false);
                return;
            }
            // Only update logs if we have valid data (not "In Progress" or empty)
            if (deviceLogs.data !== 'In Progress' && deviceLogs.data !== '' && deviceLogs.data !== 'Timeout') {
                // Handle both string and object responses
                const logData = typeof deviceLogs.data === 'object' ? deviceLogs.data?.Data : deviceLogs.data;
                if (logData && logData !== 'In Progress' && logData !== '' && !hasError(deviceLogs.data)) {
                    setLogs(logData);
                }
                setLocalLogsLoading(false); // Clear loading when data is received
            }
        }
    }, [deviceLogs]);
    
    // Clear local loading states when operations complete or error
    useEffect(() => {
        // Clear communication loading when data is received (not "In Progress") or on error
        // Check for Error field in data object, error field, or Timeout status
        if (deviceCommunication) {
            // Check for Error field in data.data (API response structure)
            const hasError = deviceCommunication.data?.Error || 
                           deviceCommunication.data?.data?.Error ||
                           deviceCommunication.error || 
                           deviceCommunication.status === 'Timeout';
            
            if (hasError) {
                // Clear loading immediately if there's an error
                setLocalCommLoading(false);
            } else if (deviceCommunication.data) {
                // Check if we have a result (either success with Data or error with Error field)
                const dataObj = typeof deviceCommunication.data === 'object' ? deviceCommunication.data : { Data: deviceCommunication.data };
                
                // If data.data exists (API response structure), check that
                const apiData = dataObj.data || dataObj;
                
                // Clear if we have a result (not "In Progress")
                if (apiData.Data && apiData.Data !== 'In Progress' && apiData.Data !== '') {
                    setLocalCommLoading(false);
                } else if (dataObj.Data && dataObj.Data !== 'In Progress' && dataObj.Data !== '') {
                    setLocalCommLoading(false);
                }
            }
        }
        // Clear time loading when data is received or on error
        if (deviceTimeData?.data) {
            const dataObj = typeof deviceTimeData.data === 'object' ? deviceTimeData.data : { Data: deviceTimeData.data };
            // Check for Error field first - if present, stop loading immediately
            if (dataObj.Error || deviceTimeData.data?.Error) {
                setLocalTimeLoading(false);
            } else if (dataObj.Data && dataObj.Data !== 'In Progress' && dataObj.Data !== '' && dataObj.Data !== 'Timeout') {
                setLocalTimeLoading(false);
            }
        }
        if (deviceTimeData?.data?.Error || deviceTimeData?.error || hasError(deviceTimeData?.data) || hasError(deviceTimeData)) {
            setLocalTimeLoading(false);
        }
        // Clear set time loading when data is received or on error
        if (deviceSetTimeData?.data) {
            const dataObj = typeof deviceSetTimeData.data === 'object' ? deviceSetTimeData.data : { Data: deviceSetTimeData.data };
            // Check for Error field first - if present, stop loading immediately
            if (dataObj.Error || deviceSetTimeData.data?.Error) {
                setLocalSetTimeLoading(false);
            } else if (dataObj.Data && dataObj.Data !== 'In Progress' && dataObj.Data !== '' && dataObj.Data !== 'Timeout') {
                setLocalSetTimeLoading(false);
            }
        }
        if (deviceSetTimeData?.data?.Error || deviceSetTimeData?.error || hasError(deviceSetTimeData?.data) || hasError(deviceSetTimeData)) {
            setLocalSetTimeLoading(false);
        }
        // Clear erase logs loading when data is received or on error
        if (deviceEraseLogs?.data) {
            const dataObj = typeof deviceEraseLogs.data === 'object' ? deviceEraseLogs.data : { Data: deviceEraseLogs.data };
            // Check for Error field first - if present, stop loading immediately
            if (dataObj.Error || deviceEraseLogs.data?.Error) {
                setLocalEraseLogsLoading(false);
            } else if (dataObj.Data && dataObj.Data !== 'In Progress' && dataObj.Data !== '' && dataObj.Data !== 'Timeout') {
                setLocalEraseLogsLoading(false);
            }
        }
        if (deviceEraseLogs?.data?.Error || deviceEraseLogs?.error || hasError(deviceEraseLogs?.data) || hasError(deviceEraseLogs)) {
            setLocalEraseLogsLoading(false);
        }
        // Clear weld params loading when download completes or fails
        if (deviceWeldParams?.data && typeof deviceWeldParams.data === 'string') {
            if (deviceWeldParams.data.includes('downloaded successfully') || 
                deviceWeldParams.data.includes('failed') ||
                deviceWeldParams.data.includes('Timeout')) {
                setLocalWeldParamsLoading(false);
            }
        }
        if (deviceWeldParams?.error) {
            setLocalWeldParamsLoading(false);
        }
        // Clear logs loading when error is detected
        if (deviceLogs?.data?.Error || deviceLogs?.error || hasError(deviceLogs?.data) || hasError(deviceLogs)) {
            setLocalLogsLoading(false);
        }
        // Clear location loading when data is received or on error
        if (deviceLocationData?.data) {
            const dataObj = typeof deviceLocationData.data === 'object' ? deviceLocationData.data : { Data: deviceLocationData.data };
            // Check for Error field first - if present, stop loading immediately
            if (dataObj.Error || deviceLocationData.data?.Error) {
                setLocalLocationLoading(false);
            } else if (dataObj.Data && dataObj.Data !== 'In Progress' && dataObj.Data !== '' && dataObj.Data !== 'Timeout') {
                setLocalLocationLoading(false);
            }
        }
        if (deviceLocationData?.data?.Error || deviceLocationData?.error || hasError(deviceLocationData?.data) || hasError(deviceLocationData)) {
            setLocalLocationLoading(false);
        }
    }, [deviceCommunication, deviceTimeData, deviceSetTimeData, deviceLogs, deviceEraseLogs, deviceWeldParams, deviceLocationData]);

    // Update location state when deviceLocationData changes
    useEffect(() => {
        if (deviceLocationData?.data) {
            // Check for Error field first - if present, don't try to parse
            if (hasError(deviceLocationData.data) || deviceLocationData.data?.Error) {
                return;
            }
            if (deviceLocationData.data !== 'In Progress' && deviceLocationData.data !== '' && deviceLocationData.data !== 'Timeout') {
                try {
                    // Handle both string and object responses
                    const locationStr = typeof deviceLocationData.data === 'object' ? deviceLocationData.data?.Data : deviceLocationData.data;
                    if (locationStr && typeof locationStr === 'string') {
                        const locationData = JSON.parse(locationStr);
                        setDeviceLocation({
                            station: locationData.Station || "-",
                            type: locationData.BugType || "-",
                            number: locationData.BugNum || "-"
                        });
                    }
                } catch (error) {
                    // Ignore parsing errors
                }
            }
        }
    }, [deviceLocationData]);

    // Show download modal when weld params are successfully retrieved
    useEffect(() => {
        //console.log('useEffect triggered - deviceWeldParams:', deviceWeldParams);
        //console.log('downloadingParams:', downloadingParams, 'currentDownloadId:', currentDownloadId);

        // Handle weld parameter download progress updates
        if (deviceWeldParams?.data && downloadingParams && currentDownloadId) {
            // Handle both string and JSON response formats
            let weldParamData = deviceWeldParams.data;
            if (typeof weldParamData === 'object' && weldParamData.data) {
                weldParamData = weldParamData.data;
              //  console.log('EdgeDeviceManagement - Extracted message from JSON:', weldParamData);
            }
            
            if (typeof weldParamData === 'string') {
                // Skip if this is the same message we already processed
                if (weldParamData === lastProcessedMessageRef.current) {
                   // console.log('Skipping duplicate message:', weldParamData);
                    return;
                }
             //   console.log('Processing new message:', weldParamData);
                lastProcessedMessageRef.current = weldParamData;
                // Check if download is completed successfully
                if (weldParamData.includes('Weld Parameter has been downloaded successfully')) {
                  //  console.log('Weld parameter download completed successfully');
                    setShowDownloadModal(true);
                    setDownloadingParams(false);
                    // Complete the download in progress tracking
                    completeDownload(currentDownloadId);
                    setCurrentDownloadId(null);
                    // Resume auto-refresh after download completes
                    if (startAutoRefresh) {
                        // console.log('startAutoRefresh called after download completes');
                        startAutoRefresh();
                    }
                }
                // Check if download failed (same message for 60 seconds)
                else if (weldParamData.includes('Weld parameter download failed - same message for 60 seconds')) {
                  //  console.log('Weld parameter download failed - same message for 60 seconds');
                    setDownloadingParams(false);
                    // Fail the download in progress tracking
                    failDownload(currentDownloadId, 'Download failed - same message for 60 seconds');
                    setCurrentDownloadId(null);
                    // Resume auto-refresh after download fails
                    if (startAutoRefresh) {
                        console.log('startAutoRefresh called after download fails');
                        startAutoRefresh();
                    }
                }
                // Check if download is initiated
                else if (weldParamData.includes('Weld Parameter Download is initiated, please check back later')) {
               //     console.log('Weld parameter download initiated');
                    // Update progress to show initiated status
                    updateProgress(currentDownloadId, {
                        status: 'in_progress',
                        currentFile: weldParamData,
                        progress: 0
                    });
                }
                // Check for chunk progress updates (format: "5 out of 40 chunks processed")
                else if (weldParamData.includes('out of') && weldParamData.includes('chunks processed')) {
                    // Parse chunk progress - handles both "Chunks - 5 out of 40 chunks processed" and "5 out of 40 chunks processed"
                    const chunkMatch = weldParamData.match(/(\d+)\s+out\s+of\s+(\d+)\s+chunks\s+processed/);
                    if (chunkMatch) {
                        const currentChunk = parseInt(chunkMatch[1]);
                        const totalChunks = parseInt(chunkMatch[2]);
                        const progress = Math.round((currentChunk / totalChunks) * 100);
                        
                      //  console.log(`Progress: ${currentChunk}/${totalChunks} chunks (${progress}%)`);
                        
                        // If all chunks are processed, mark as completed
                        if (currentChunk >= totalChunks) {
                         //   console.log('All chunks processed, marking download as completed');
                            completeDownload(currentDownloadId);
                            setDownloadingParams(false);
                            setCurrentDownloadId(null);
                            lastProcessedMessageRef.current = '';
                           
                        } else {
                            // Force update by ensuring we always create a new state
                            updateProgress(currentDownloadId, {
                                status: 'in_progress',
                                currentFile: weldParamData,
                                progress: progress,
                                totalFiles: totalChunks,
                                downloadedFiles: currentChunk
                            });
                            // Force a re-render by updating a timestamp
                         //   console.log('Progress updated - should trigger UI update');
                        }
                    } else {
                        // Update with raw message
                        updateProgress(currentDownloadId, {
                            status: 'in_progress',
                            currentFile: weldParamData
                        });
                    }
                }
                // Handle other progress messages
                else if (weldParamData.includes('In Progress') || weldParamData.includes('Processing')) {
                  //  console.log('Weld parameter download in progress:', weldParamData);
                    updateProgress(currentDownloadId, {
                        status: 'in_progress',
                        currentFile: weldParamData
                    });
                }
            }
        }

        // Handle errors
        if (deviceWeldParams?.error && downloadingParams && currentDownloadId) {
       //     console.log('Weld params error:', deviceWeldParams.error);
            failDownload(currentDownloadId, deviceWeldParams.error);
            setCurrentDownloadId(null);
            setDownloadingParams(false);
            // Resume auto-refresh after download error
            if (startAutoRefresh) {
                startAutoRefresh();
               // console.log('startAutoRefresh called');
            }
        }
    }, [deviceWeldParams, weldParamsUpdateKey, downloadingParams, currentDownloadId, completeDownload, failDownload, updateProgress, startAutoRefresh]);



    // Error State Component
    const ErrorState = ({ message }: { message: string }) => (
        <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
            <AlertTriangle className="h-4 w-4 text-hero-red flex-shrink-0" />
            <p className="text-[13px] text-hero-red">{message}</p>
        </div>
    );

    // console.log('apiResponse?.lastActuals?.createdAt', apiResponse?.lastActuals?.timestamp);
    // console.log('formatDateUTC(apiResponse?.lastActuals?.createdAt)', formatLocalTimestamp(apiResponse?.lastActuals?.timestamp));
    return (
       
        <div className="bg-page-bg min-h-screen">

            {/* Main Content */}
            <div className="max-w-8xl mx-auto p-6">
                {/* Compact device header band */}
                <div className="bg-white border border-[#E2E5EA] rounded-lg mb-4">
                    <div className="px-5 py-3 flex items-start gap-4">
                        <button
                            type="button"
                            onClick={handleBackNavigation}
                            className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg border border-[#E2E5EA] bg-white hover:bg-page-bg transition-colors text-[12px] font-medium text-text-grey"
                        >
                            <ChevronLeft className="h-3.5 w-3.5 text-blueprint-blue" />
                            Devices
                        </button>

                        <div className="flex-1 min-w-0">
                            {/* Title row: device id + status chips */}
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                                <span className="text-[15px] font-mono font-semibold text-blueprint-blue">{device.id}</span>

                                {device.communication_status && (
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${
                                        device.communication_status === 'Online'
                                            ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                            : 'text-readable-red bg-[rgba(196,0,31,0.06)]'
                                    }`}>
                                        <span className={`h-1.5 w-1.5 rounded-full ${
                                            device.communication_status === 'Online' ? 'bg-hero-red' : 'bg-hero-red'
                                        }`}></span>
                                        {device.communication_status === 'Online' ? 'Online' : 'Offline'}
                                    </span>
                                )}

                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium ${getStatusBadgeColor(device.status)}`}>
                                    {getStatusName(device.status)}
                                </span>

                                {device.project?.name && (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium text-blueprint-blue bg-[rgba(0,39,63,0.06)]">
                                        <Building2 className="w-3 h-3" />
                                        {device.project.name}
                                    </span>
                                )}
                            </div>

                            {/* Subtitle row: meta details (Device ID excluded — already in title) */}
                            <div className="text-[11px] text-heritage-grey flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                {infoItems.filter((item) => item.label !== 'Device ID').map((item, idx) => (
                                    <span key={idx}>
                                        <span className="font-medium">{item.label}:</span> {item.value}
                                    </span>
                                ))}
                                {device.EdgeFWVersion && device.EdgeFWVersion !== '-' && (
                                    <span><span className="font-medium">Firmware:</span> v{device.EdgeFWVersion}</span>
                                )}
                                {device.EdgeFWVersion === '-' && (
                                    <span className="inline-flex items-center gap-1">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Loading version...
                                    </span>
                                )}
                                {device?.lastSeen && (
                                    <span><span className="font-medium">Last seen:</span> {formatDateUTC(device.lastSeen)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Auto-loading Indicator */}
                {/* {(communicationLoading || timeLoading || logsManagementLoading || locationLoading) && (
                    <div className={`border rounded-lg p-4 mb-6 ${isInitialLoad ? 'bg-blue-50' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center gap-3">
                            <Loader2 className={`h-5 w-5 animate-spin ${isInitialLoad ? 'text-blue-600' : 'text-gray-600'}`} />
                            <div>
                                <h4 className={`text-sm font-medium ${isInitialLoad ? 'text-blue-900' : 'text-gray-900'}`}>
                                    {isInitialLoad ? 'Initializing Device Connection' : 'Loading Device Data'}
                                </h4>
                                <p className={`text-xs mt-1 ${isInitialLoad ? 'text-blue-700' : 'text-gray-700'}`}>
                                    {isInitialLoad ? 'Testing device connection first...' : (
                                        <>
                                            {communicationLoading && "Testing connection... "}
                                            {!communicationLoading && communicationData?.connection_status === 'Online' && "Connection successful! "}
                                            {timeLoading && "Getting device time... "}
                                            {logsManagementLoading && "Reading log count... "}
                                            {locationLoading && "Getting location data... "}
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                )} */}

                {/* Tab Navigation */}
                <Tabs value={activeTab} onValueChange={(value) => {
                    console.log('Tab changing from', activeTab, 'to', value);
                    // Immediately clear interval when switching away from operations
                    if (activeTab === 'operations' && value !== 'operations' && temperaturePollingIntervalRef.current) {
                        console.log('🚨 Tab switching away from operations - clearing interval NOW');
                        clearInterval(temperaturePollingIntervalRef.current);
                        temperaturePollingIntervalRef.current = null;
                    }
                    setActiveTab(value);
                }} className="w-full">
                    <TabsList className="h-auto w-full justify-start gap-2 border-b-2 border-[#E2E5EA] bg-transparent px-0 py-0 rounded-none">
                        {tabs.map((tab) => (
                            <TabsTrigger
                                key={tab}
                                value={tab.toLowerCase()}
                                className="px-6 py-2.5 text-[13px] font-semibold text-heritage-grey hover:text-blueprint-blue border-b-2 border-transparent -mb-[2px] rounded-none data-[state=active]:text-blueprint-blue data-[state=active]:border-hero-red data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            >
                                {tab}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {/* Tab Content */}
                    <TabsContent value="control">
                        <>
                            {/* Primary Device Control */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                                {/* Power & System Control */}
                                <div className="bg-white rounded-lg border border-[#E2E5EA]">
                                    <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                        <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                                            <svg className="w-4 h-4 text-heritage-grey" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                            </svg>
                                            Power Control
                                        </h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                                                <p className="text-[13px] font-medium text-text-grey">Device Status</p>
                                                <div className="flex items-center gap-2 text-[13px]">
                                                    {device.weldMachineConnected ? (
                                                        <>
                                                            <span className="h-1.5 w-1.5 bg-hero-red rounded-full"></span>
                                                            <span className="text-text-grey">{WeldTypeNames[device.metadata.weldType as keyof typeof WeldTypeNames] || device.metadata.weldType}</span>
                                                            {device.metadata?.weldUID && (
                                                                <span className="text-[12px] font-medium text-galvanizing-green">UID: {device.metadata.weldUID}</span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="h-1.5 w-1.5 bg-[#B38600] rounded-full"></span>
                                                            <span className="text-[#B38600]">Welding Machine not connected</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-2 pt-4">
                                                <Button
                                                    onClick={() => handleUpdateOperationStatus('Running')}
                                                    disabled={isAnyOperationLoading || true}
                                                    className="flex-1 bg-hero-red hover:opacity-90 text-white"
                                                >
                                                    <Activity className="w-4 h-4 mr-2" />
                                                    Start
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleUpdateOperationStatus('Stopped')}
                                                    disabled={isAnyOperationLoading || true}
                                                    className="flex-1 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                                                >
                                                    <XCircle className="w-4 h-4 mr-2" />
                                                    Stop
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={() => handleUpdateOperationStatus('Restarting')}
                                                    disabled={isAnyOperationLoading || true}
                                                    className="flex-1 border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                                                >
                                                    <RefreshCw className="w-4 h-4 mr-2" />
                                                    Restart
                                                </Button>
                                                {onSettingsClick && (
                                                    <Button
                                                        onClick={onSettingsClick}
                                                        disabled={isAnyOperationLoading}
                                                        className="bg-hero-red hover:bg-hero-red-60 text-white"
                                                    >
                                                        <Cog className="h-3.5 w-3.5 mr-2" />
                                                        Decommission
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Connection & Communication */}
                                <div className="bg-white rounded-lg border border-[#E2E5EA]">
                                    <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                        <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                                            <svg className="w-4 h-4 text-heritage-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                                            </svg>
                                            Connectivity
                                        </h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                {(() => {
                                                    const isConnected = (!isDeviceCommunicationLoading && communicationData?.connection_status && communicationData.connection_status == 'Online') || (device.communication_status == 'Online');
                                                    return (
                                                        <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 text-center">
                                                            <div className="text-[11px] text-heritage-grey mb-1.5">Network Status</div>
                                                            {isConnected ? (
                                                                <div className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-galvanizing-green">
                                                                    <span className="h-1.5 w-1.5 rounded-full bg-hero-red"></span>
                                                                    Connected
                                                                </div>
                                                            ) : (
                                                                <div className="text-[14px] font-medium text-heritage-grey">—</div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                                <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 text-center">
                                                    <div className="text-[11px] text-heritage-grey mb-1.5">Response Time</div>
                                                    {!isDeviceCommunicationLoading && deviceResponseTime ? (
                                                        <div className="text-[14px] font-semibold text-blueprint-blue">{formatResponseTime(deviceResponseTime)}</div>
                                                    ) : (
                                                        <div className="text-[14px] font-medium text-heritage-grey">—</div>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    setCommCheckStartTime(Date.now());
                                                    setIsConnected(true);
                                                    setLastCommCheck(new Date().toLocaleString());
                                                    handleCommunicationCheck();
                                                }}
                                                disabled={isDeviceCommunicationLoading || isAnyOperationLoading}
                                                className="self-start bg-hero-red hover:bg-hero-red-60 text-white"
                                            >
                                                {isDeviceCommunicationLoading ? (
                                                    <>
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                                                        Testing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <RefreshCcw className="h-3.5 w-3.5 mr-2" />
                                                        Test Connection
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Actions & Settings */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Time Synchronization */}
                                <div className="bg-white rounded-lg border border-[#E2E5EA]">
                                    <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                        <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                                            <svg className="w-4 h-4 text-heritage-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M12 6v6l3 3" />
                                            </svg>
                                            Time Sync
                                        </h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 gap-4">
                                                {(() => {
                                                    const time =
                                                        !isDeviceTimeLoading && deviceTimeData?.data && deviceTimeData.data !== 'In Progress' && deviceTimeData.data !== '' && deviceTimeData.data !== 'Timeout'
                                                            ? formatDeviceTime(deviceTimeData.data)
                                                            : !isDeviceSetTimeLoading && deviceSetTimeData?.data && deviceSetTimeData.data !== 'In Progress' && deviceSetTimeData.data !== '' && deviceSetTimeData.data !== 'Timeout'
                                                                ? formatDeviceTime(deviceSetTimeData.data)
                                                                : null;
                                                    return (
                                                        <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-6 text-center">
                                                            <div className="text-[11px] text-heritage-grey mb-1.5">Device Time</div>
                                                            {time ? (
                                                                <div className="text-[16px] font-semibold text-blueprint-blue">{time}</div>
                                                            ) : (
                                                                <div className="inline-flex items-center gap-2 text-[13px] text-heritage-grey">
                                                                    <ClockIcon className="h-4 w-4" />
                                                                    Click <span className="font-medium text-text-grey">Get Time</span> to retrieve device time.
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <Button
                                                    size="sm"
                                                    onClick={handleGetTime}
                                                    disabled={!device.weldMachineConnected || isAnyOperationLoading || isDeviceTimeLoading}
                                                    className="bg-hero-red hover:bg-hero-red-60 text-white"
                                                >
                                                    {isDeviceTimeLoading ? (
                                                        <>
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                                                            Getting...
                                                        </>
                                                    ) : 'Get Time'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleSetTime}
                                                    disabled={!device.weldMachineConnected || isAnyOperationLoading || isDeviceSetTimeLoading}
                                                    className="border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                                                >
                                                    {isDeviceSetTimeLoading ? (
                                                        <>
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                                                            Syncing...
                                                        </>
                                                    ) : 'Sync Now'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Logs & Location */}
                                <div className="bg-white rounded-lg border border-[#E2E5EA]">
                                    <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                        <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                                            <svg className="w-4 h-4 text-heritage-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Logs & Location
                                        </h3>
                                    </div>
                                    <div className="p-6">
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3 text-center flex flex-col justify-center">
                                                    <div className="text-[11px] text-heritage-grey mb-1.5">Active Logs</div>
                                                    {!isDeviceLogsLoading ? (
                                                        <div className="text-[20px] font-semibold text-blueprint-blue">{getLogCount()}</div>
                                                    ) : (
                                                        <div className="text-[14px] font-medium text-heritage-grey">—</div>
                                                    )}
                                                </div>
                                                <div className="bg-page-bg border border-[#E2E5EA] rounded-lg p-3">
                                                    <div className="text-[11px] text-heritage-grey mb-1.5">Device Location</div>
                                                    <div className="space-y-0.5 text-[12px]">
                                                        <div><span className="text-heritage-grey">Station:</span> <span className="font-medium text-text-grey">{getLocationData('Station')}</span></div>
                                                        <div><span className="text-heritage-grey">Bug Type:</span> <span className="font-medium text-text-grey">{getLocationData('BugType')}</span></div>
                                                        <div><span className="text-heritage-grey">Bug Number:</span> <span className="font-medium text-text-grey">{getLocationData('BugNum')}</span></div>
                                                    </div>
                                                </div>
                                            </div>



                                            <div className="grid grid-cols-4 gap-3">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleCheckLogs}
                                                    disabled={!device.weldMachineConnected || isAnyOperationLoading || isDeviceLogsLoading}
                                                    className="border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
                                                >
                                                    {isDeviceLogsLoading ? (
                                                        <>
                                                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                            Reading...
                                                        </>
                                                    ) : 'Read Log Count'}
                                                </Button>

                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setDownloadingParams(true);
                                                        handleWeldParams();
                                                    }}
                                                    disabled={!device.weldMachineConnected || downloadingParams || isAnyOperationLoading || isDeviceWeldParamsLoading}
                                                    className="border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
                                                >
                                                    {isDeviceWeldParamsLoading ? (
                                                        <>
                                                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                            Downloading...
                                                        </>
                                                    ) : 'Download Parameter'}
                                                </Button>

                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setIsEraseLogsConfirmOpen(true)}
                                                    disabled={!device.weldMachineConnected || erasing || isAnyOperationLoading || isDeviceEraseLogsLoading}
                                                    className="border-[rgba(235,28,55,0.2)] text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.06)] text-[12px]"
                                                >
                                                    {isDeviceEraseLogsLoading ? (
                                                        <>
                                                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                            Erasing...
                                                        </>
                                                    ) : 'Erase logs'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleLocation}
                                                    disabled={!device.weldMachineConnected || isAnyOperationLoading || isDeviceLocationLoading}
                                                    className="border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
                                                >
                                                    {isDeviceLocationLoading ? (
                                                        <>
                                                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                            Getting...
                                                        </>
                                                    ) : 'Get Location'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            {/* Error Messages - Only show errors for this device */}
                            {(deviceCommunicationError || deviceTimeError || deviceSetTimeError || deviceLogsError || deviceEraseLogsError || deviceWeldParamsError || deviceLocationError ||
                                deviceTimeData?.data?.error || deviceSetTimeData?.data?.error || deviceLogs?.data?.error || deviceEraseLogs?.data?.error || deviceWeldParams?.data?.error || deviceLocationData?.data?.error ||
                                device.get_time?.error || device.set_time?.error || device.number_of_logs?.error || device.erase_logs?.error || device.weld_parameter_logs?.error || deviceLocationPropError) && (
                                    <div className="bg-white rounded-lg border border-[#E2E5EA] p-6">
                                        <div className="space-y-4">
                                            <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-hero-red" />
                                                Operation Errors — Device {device.id}
                                            </h3>
                                            <div className="space-y-3">
                                                {deviceCommunicationError && <ErrorState message={`Communication : ${deviceCommunicationError}`} />}
                                                {deviceTimeError && <ErrorState message={`Get time : ${deviceTimeError}`} />}
                                                {deviceSetTimeError && <ErrorState message={`Set time : ${deviceSetTimeError}`} />}
                                                {deviceLogsError && <ErrorState message={`Log count : ${deviceLogsError}`} />}
                                                {deviceEraseLogsError && <ErrorState message={`Erase logs : ${deviceEraseLogsError}`} />}
                                                {deviceWeldParamsError && <ErrorState message={`Parameters : ${deviceWeldParamsError}`} />}
                                                {deviceLocationError && <ErrorState message={`Location : ${deviceLocationError}`} />}

                                                {/* Device-specific errors from API response */}
                                                {deviceTimeData?.data?.error && <ErrorState message={`Device Get Time Error: ${deviceTimeData.data.error}`} />}
                                                {deviceSetTimeData?.data?.error && <ErrorState message={`Device Set Time Error: ${deviceSetTimeData.data.error}`} />}
                                                {deviceLogs?.data?.error && <ErrorState message={`Device Log Count Error: ${deviceLogs.data.error}`} />}
                                                {deviceEraseLogs?.data?.error && <ErrorState message={`Device Erase Logs Error: ${deviceEraseLogs.data.error}`} />}
                                                {deviceWeldParams?.data?.error && <ErrorState message={`Device Parameters Error: ${deviceWeldParams.data.error}`} />}
                                                {deviceLocationData?.data?.error && <ErrorState message={`Device Location Error: ${deviceLocationData.data.error}`} />}
                                                
                                                {/* Device prop errors (from device object directly) */}
                                                {device.get_time?.error && !deviceTimeError && <ErrorState message={`Get Time Error: ${device.get_time.error}`} />}
                                                {device.set_time?.error && !deviceSetTimeError && <ErrorState message={`Set Time Error: ${device.set_time.error}`} />}
                                                {device.number_of_logs?.error && !deviceLogsError && <ErrorState message={`Log Count Error: ${device.number_of_logs.error}`} />}
                                                {device.erase_logs?.error && !deviceEraseLogsError && <ErrorState message={`Erase Logs Error: ${device.erase_logs.error}`} />}
                                                {device.weld_parameter_logs?.error && !deviceWeldParamsError && <ErrorState message={`Parameters Error: ${device.weld_parameter_logs.error}`} />}
                                                {deviceLocationPropError && !deviceLocationError && <ErrorState message={`Location Error: ${deviceLocationPropError}`} />}
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </>
                    </TabsContent>

                    <TabsContent value="firmware">
                        <div className="bg-white rounded-lg border border-[#E2E5EA]">
                            <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                <h3 className="text-[14px] font-semibold text-blueprint-blue">Firmware Management</h3>
                            </div>
                            <div className="p-2">
                                <FirmwarePage 
                                 startAutoRefresh={startAutoRefresh}
                                 stopAutoRefresh={stopAutoRefresh}
                                    device={device}
                                    refetchDevice={refetchDevice || (() => {
                                        // Fallback: dispatch getAllDevices to refresh device data
                                        dispatch(getAllDevices({}));
                                    })}
                                    selectedFile={selectedFile}
                                    setSelectedFile={setSelectedFile}
                                />
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="certificates">
                        <div className="bg-white rounded-lg border border-[#E2E5EA]">
                            <div className="px-6 py-4 border-b border-[#E2E5EA]">
                                <h3 className="text-[14px] font-semibold text-blueprint-blue">Security Certificates</h3>
                            </div>
                            <div className="p-6 flex flex-col h-full">
                                <div className="text-center py-12 flex-1">
                                    <Shield className="mx-auto h-10 w-10 text-heritage-grey" />
                                    <h3 className="mt-3 text-[13px] font-semibold text-blueprint-blue">Device Certificates</h3>
                                    <p className="mt-1 text-[12px] text-heritage-grey">View and manage security certificates for this device.</p>
                                </div>
                                <div className="flex justify-end">
                                    <Button size="sm" className="bg-hero-red hover:bg-hero-red-60 text-white">
                                        View Certificates
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="logs">
                        <DeviceLogDetails key={String(device.id)} device={device} />
                    </TabsContent>

                    <TabsContent value="history">
                        <DeviceProjectHistory device={device} />
                    </TabsContent>

                    <TabsContent value="operations">
                        <div className="mt-6 bg-white rounded-lg border border-[#E2E5EA] p-6">
                            <OperationsHeader
                                mode={apiResponse?.lastActuals?.record || '-'}
                                jointNumber={apiResponse?.lastActuals?.jointRef || '-'}
                                isUpdating={operationsData.isUpdating}
                                lastUpdate={apiResponse?.lastActuals?.timestamp ? formatLocalTimestamp(apiResponse?.lastActuals?.timestamp) : '-'}
                                activeAlarmCount={apiResponse?.matchedAlarmList?.length}
                            />

                            <OperationsFilters
                                showTemperatureZones={showTemperatureZones}
                                showPressureSystems={showPressureSystems}
                                showProcessData={showProcessData}
                                showAlarms={showAlarms}
                                onTemperatureZonesChange={(checked: boolean) => setShowTemperatureZones(checked)}
                                onPressureSystemsChange={(checked: boolean) => setShowPressureSystems(checked)}
                                onProcessDataChange={(checked: boolean) => setShowProcessData(checked)}
                                onAlarmsChange={(checked: boolean) => setShowAlarms(checked)}
                                temperatureRanges={temperatureRanges}
                                onTemperatureRangesChange={setTemperatureRanges}
                                edgeUid={device.metadata?.weldUID || device.id}
                                apiMinimumTemperature={apiResponse?.minimum_temperature}
                                apiUnderMinimumTemperature={apiResponse?.under_minimum_temperature}
                                apiOverMaximumTemperature={apiResponse?.over_maximum_temperature}
                                apiMaximumTemperature={apiResponse?.maximum_temperature}
                                apiMinimumPressure={apiResponse?.minimum_pressure}
                                apiUnderMinimumPressure={apiResponse?.under_minimum_pressure}
                                apiOverMaximumPressure={apiResponse?.over_maximum_pressure}
                                apiMaximumPressure={apiResponse?.maximum_pressure}
                                refreshInterval={refreshInterval}
                                onRefreshIntervalChange={setRefreshInterval}
                                onRefreshIntervalSave={updatePollingInterval}
                                onRefreshData={handleRefreshTemperatureData}
                                coatingType={device.deviceData?.coating_type || device.deviceData?.coatingType}
                                alarmDetails={apiResponse?.alarmDetails}
                                raptorConfig={raptorConfig}
                                onRaptorConfigChange={setRaptorConfig}
                            />
                        </div>

                        {/* Temperature Control Dashboard */}
                        {showTemperatureZones && (
                            isRaptorDevice ? (
                                <RaptorCoatingControlDashboard
                                    section="fbe"
                                    apiResponse={apiResponse}
                                    deviationRanges={raptorConfig.fbe}
                                />
                            ) : (
                                <TemperatureControlDashboard
                                    temperatureView={temperatureView}
                                    onViewChange={setTemperatureView}
                                    apiResponse={apiResponse}
                                    temperatureRanges={temperatureRanges}
                                />
                            )
                        )}

                        {/* Pressure Control Dashboard */}
                        {showPressureSystems && (
                            isRaptorDevice ? (
                                <RaptorCoatingControlDashboard
                                    section="cmpp"
                                    apiResponse={apiResponse}
                                    deviationRanges={raptorConfig.cmpp}
                                />
                            ) : (
                                <PressureControlDashboard
                                    pressureView={pressureView}
                                    onViewChange={setPressureView}
                                    apiResponse={apiResponse}
                                />
                            )
                        )}

                        {/* Bottom Row: Process Data + System Alarms (Raptor: alarms full width; no Process Data) */}
                        <div
                            className={`grid gap-6 mt-6 ${
                                isRaptorDevice ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'
                            }`}
                        >
                            {!isRaptorDevice && showProcessData && (
                                <ProcessData apiResponse={apiResponse} />
                            )}

                            {showAlarms && <SystemAlarms apiResponse={apiResponse} />}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Weld Parameter Download Status Modal */}
            {/* <WeldParameterDownloadStatus
                isOpen={showDownloadModal}
                onClose={() => { setShowDownloadModal(false); setDownloadingParams(false); }}
                deviceId={device.id}
                deviceName={device.name || `Device ${device.id}`}
            /> */}

            {/* Weld Parameter Download Confirmation Dialog */}
            <WeldParameterDownloadDialog
                isOpen={showWeldParamConfirmDialog}
                onClose={() => setShowWeldParamConfirmDialog(false)}
                onConfirm={handleConfirmWeldParamDownload}
                deviceName={device.name || `Device ${device.id}`}
                isLoading={weldParamsLoading}
            />




            {/* Download Progress Modal */}
            <DownloadProgressModal
                isOpen={showDownloadModal}
                onClose={() => {
                    setShowDownloadModal(false);
                    // If download is still in progress when modal is closed, resume auto-refresh
                    // (download will continue in background and auto-refresh will resume when it completes)
                    if (!downloadingParams && startAutoRefresh) {
                        startAutoRefresh();
                        console.log('startAutoRefresh called');
                    }
                }}
            />

            {/* Erase Logs confirmation dialog */}
            <Dialog open={isEraseLogsConfirmOpen} onOpenChange={(open) => { if (!open) setIsEraseLogsConfirmOpen(false); }}>
                <DialogContent className="max-w-md p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex-shrink-0 w-10 h-10 bg-[rgba(235,28,55,0.1)] rounded-full flex items-center justify-center">
                            <Trash className="h-5 w-5 text-hero-red" />
                        </div>
                        <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
                            Erase device logs?
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-[13px] text-text-grey">
                        All logs will be permanently removed from the device. This action cannot be undone.
                    </DialogDescription>

                    <div className="mt-4 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-md">
                        <p className="text-[13px] text-hero-red">
                            <span className="font-semibold">Warning:</span> Once erased, log history for this device cannot be recovered.
                        </p>
                    </div>

                    <DialogFooter className="gap-3 mt-6">
                        <Button
                            variant="outline"
                            onClick={() => setIsEraseLogsConfirmOpen(false)}
                            disabled={erasing || isDeviceEraseLogsLoading}
                            className="min-w-[100px] border-[#E2E5EA] text-text-grey hover:bg-page-bg"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                setIsEraseLogsConfirmOpen(false);
                                setErasing(true);
                                await new Promise((r) => setTimeout(r, 1000));
                                setLogs(0);
                                setErasing(false);
                                handleEraseLogs();
                            }}
                            disabled={erasing || isDeviceEraseLogsLoading}
                            className="min-w-[100px] bg-hero-red hover:bg-hero-red-60 text-white"
                        >
                            Erase logs
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EdgeDeviceManagement;


